import { ComponentProps, ReactElement } from 'react';

import { styled } from '@mui/material';

import { removeTheseProps } from '@/utils';

const Container = styled('ul')(({ theme: { palette } }) => ({
  padding: 0,
  minHeight: 75,
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  border: `1.2px solid ${palette.horizonGray3.main}`,
  borderRadius: 8,
  overflow: 'hidden',
  '& :last-child': {
    borderRight: 'none',
  },
  transition: 'all 300ms',
}));

type StepProps = ComponentProps<'li'> & {
  active: boolean;
  completed: boolean;
};

const Step = styled('li', {
  shouldForwardProp: removeTheseProps<StepProps>('active', 'completed'),
})<StepProps>(({ theme: { palette }, active, completed }) => ({
  display: 'grid',
  placeItems: 'center',
  backgroundColor: active ? palette.common.white : palette.horizonGray4.main,
  border: 'none',
  borderBottom: `3px solid ${completed ? palette.horizonGreen.main : palette.horizonBlue2.main}`,
  borderRight: `1.2px solid ${palette.horizonGray3.main}`,
  transition: 'all 300ms',
}));

const Title = styled('p')(({ theme: { palette } }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: palette.text.primary,
}));

export type ReportBuilderStep<Value extends string | number> = {
  title: Capitalize<string>;
  id: Value;
  [key: PropertyKey]: unknown;
};

type ReportBuilderStepperProps<Value extends string | number> = {
  steps: ReportBuilderStep<Value>[];
  value: Value;
};

export const ReportBuilderStepper = <Value extends string | number>(
  props: ReportBuilderStepperProps<Value>,
): ReactElement => {
  const currentValueIndex = props.steps.findIndex(
    (step) => step.id === props.value,
  );

  return (
    <Container>
      {props.steps.map((step, index) => {
        const key = `${step.id}-${index}`;
        const isCompleted = index < currentValueIndex;
        const isActive = step.id === props.value;

        return (
          <Step key={key} active={isActive} completed={isCompleted}>
            <Title>{step.title}</Title>
          </Step>
        );
      })}
    </Container>
  );
};
